import React from "react";
import styled, { css, keyframes } from "styled-components";
import { space, layout, SpaceProps, LayoutProps } from "styled-system";
import { Box } from "@bca/ui-library";

interface SkeletonProps extends SpaceProps, LayoutProps {
  variant: "circle" | "text";
  animationDuration?: number;
  animationDelay?: number;
}

interface SkeletonBoxProps {
  animationDuration: number;
  animationDelay: number;
}

const loadAnimation = keyframes`
  0% {
    left: -200%;
  }

  100% {
    left: 100%;
  }
`;

const animation = (props) =>
  css`
    ${loadAnimation} ${props.animationDuration}s ease-in-out infinite;
  `;

const TextSkeleton = styled(Box)<SkeletonBoxProps>`
  ${space}
  ${layout}
  background-color: #d8e1e8;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    display: block;
    position: absolute;
    left: -200%;
    top: 0;
    height: 100%;
    width: 200%;
    background: linear-gradient(
      to right,
      #d8e1e8 0%,
      #f1f4f6 50%,
      #d8e1e8 100%
    );
    animation: ${animation};
    ${(props) => `animation-delay: ${props.animationDelay}s;`}
  }
`;

const CircleSkeleton = styled(Box)<SkeletonBoxProps>`
  ${space}
  ${layout}
  background: #d8e1e8;
  border-radius: 50%;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    display: block;
    position: absolute;
    left: -200%;
    top: 0;
    height: 100%;
    width: 200%;
    background: linear-gradient(
      to right,
      #d8e1e8 0%,
      #f1f4f6 50%,
      #d8e1e8 100%
    );
    animation: ${animation};
    ${(props) => `animation-delay: ${props.animationDelay}s;`}
  }
`;

const Skeleton: React.FunctionComponent<SkeletonProps> = ({
  width,
  height,
  variant,
  animationDuration = 2,
  animationDelay = 0,
  ...rest
}) => {
  return variant === "text" ? (
    <TextSkeleton
      animationDuration={animationDuration}
      animationDelay={animationDelay}
      width={width}
      height={height}
      {...rest}
    />
  ) : (
    <CircleSkeleton
      animationDuration={animationDuration}
      animationDelay={animationDelay}
      width={width}
      height={height}
      {...rest}
    />
  );
};

export default Skeleton;
